import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Edit2, Trash2, Stethoscope, Phone, Mail, IndianRupee, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyDoctor = {
  name: '',
  specialization: '',
  phone: '',
  email: '',
  commission_percentage: '0',
  address: ''
};

function Doctors() {
  const [doctors, setDoctors] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [formData, setFormData] = useState(emptyDoctor);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchDoctors = useCallback(async function() {
    try {
      const params = searchTerm ? `?search=${searchTerm}` : '';
      const response = await axios.get(`${API}/doctors${params}`);
      setDoctors(response.data);
    } catch (error) {
      toast.error('Failed to fetch doctors');
    }
  }, [searchTerm]);

  useEffect(function() {
    fetchDoctors();
  }, [fetchDoctors]);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        name: formData.name,
        specialization: formData.specialization,
        phone: formData.phone,
        email: formData.email,
        commission_percentage: parseFloat(formData.commission_percentage),
        address: formData.address
      };

      if (editingDoctor) {
        await axios.put(`${API}/doctors/${editingDoctor.id}`, data);
        toast.success('Doctor updated');
      } else {
        await axios.post(`${API}/doctors`, data);
        toast.success('Doctor added');
      }
      
      setShowAddModal(false);
      setEditingDoctor(null);
      setFormData(emptyDoctor);
      fetchDoctors();
    } catch (error) {
      const msg = error.response && error.response.data && error.response.data.detail;
      toast.error(msg || 'Operation failed');
    } finally {
      setLoading(false);
    }
  }

  function handleEdit(doctor) {
    setEditingDoctor(doctor);
    setFormData({
      name: doctor.name,
      specialization: doctor.specialization,
      phone: doctor.phone,
      email: doctor.email,
      commission_percentage: doctor.commission_percentage.toString(),
      address: doctor.address
    });
    setShowAddModal(true);
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this doctor?')) return;
    try {
      await axios.delete(`${API}/doctors/${id}`);
      toast.success('Doctor deleted');
      fetchDoctors();
    } catch (error) {
      toast.error('Failed to delete');
    }
  }

  function handleModalClose(open) {
    setShowAddModal(open);
    if (!open) {
      setEditingDoctor(null);
      setFormData(emptyDoctor);
    }
  }

  function updateFormField(field, value) {
    const updated = { ...formData };
    updated[field] = value;
    setFormData(updated);
  }

  function calculateTotalReferrals() {
    let total = 0;
    for (let i = 0; i < doctors.length; i++) {
      total += doctors[i].total_referrals;
    }
    return total;
  }

  function calculateTotalCommission() {
    let total = 0;
    for (let i = 0; i < doctors.length; i++) {
      total += doctors[i].total_commission;
    }
    return total;
  }

  const totalReferrals = calculateTotalReferrals();
  const totalCommission = calculateTotalCommission();

  function renderDoctorCards() {
    if (doctors.length === 0) {
      return (
        <div className="col-span-full text-center py-12 text-slate-400">
          <Stethoscope className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg">No doctors registered yet</p>
          <p className="text-sm">Add referring doctors to track commissions</p>
        </div>
      );
    }

    const cards = [];
    for (let i = 0; i < doctors.length; i++) {
      const doctor = doctors[i];
      const initial = doctor.name.charAt(0);
      cards.push(
        <Card key={doctor.id} className="doctor-card" data-testid={`doctor-card-${doctor.id}`}>
          <CardContent className="p-4">
            <div className="flex justify-between items-start mb-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-mint-100 rounded-full flex items-center justify-center text-mint-700 font-bold text-lg">
                  {initial}
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-navy-900">{doctor.name}</h3>
                  <p className="text-sm text-slate-500">{doctor.specialization || 'General'}</p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={function() { handleEdit(doctor); }}
                  data-testid={`edit-doctor-${doctor.id}`}
                >
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-500 hover:text-red-700"
                  onClick={function() { handleDelete(doctor.id); }}
                  data-testid={`delete-doctor-${doctor.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-2 text-sm mb-4">
              {doctor.phone && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="w-4 h-4" />
                  <span>{doctor.phone}</span>
                </div>
              )}
              {doctor.email && (
                <div className="flex items-center gap-2 text-slate-600">
                  <Mail className="w-4 h-4" />
                  <span className="truncate">{doctor.email}</span>
                </div>
              )}
            </div>

            <div className="border-t pt-3 mt-3">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-lg font-bold text-navy-900">{doctor.commission_percentage}%</p>
                  <p className="text-xs text-slate-500">Commission</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-mint-600">{doctor.total_referrals}</p>
                  <p className="text-xs text-slate-500">Referrals</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-amber-600">₹{doctor.total_commission.toFixed(0)}</p>
                  <p className="text-xs text-slate-500">Earned</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      );
    }
    return cards;
  }

  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-heading font-bold text-navy-900">Doctor Referrals</h1>
          <p className="text-slate-500 text-sm">Manage referring doctors & commissions</p>
        </div>
        <Dialog open={showAddModal} onOpenChange={handleModalClose}>
          <DialogTrigger asChild>
            <Button className="bg-mint-600 hover:bg-mint-700" data-testid="add-doctor-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Doctor
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editingDoctor ? 'Edit Doctor' : 'Add New Doctor'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  data-testid="doctor-name"
                  value={formData.name}
                  onChange={function(e) { updateFormField('name', e.target.value); }}
                  placeholder="Dr. Full Name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="specialization">Specialization</Label>
                <Input
                  id="specialization"
                  data-testid="doctor-specialization"
                  value={formData.specialization}
                  onChange={function(e) { updateFormField('specialization', e.target.value); }}
                  placeholder="e.g., General Physician"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    data-testid="doctor-phone"
                    value={formData.phone}
                    onChange={function(e) { updateFormField('phone', e.target.value); }}
                    placeholder="9876543210"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="commission">Commission %</Label>
                  <Input
                    id="commission"
                    type="number"
                    step="0.1"
                    data-testid="doctor-commission"
                    value={formData.commission_percentage}
                    onChange={function(e) { updateFormField('commission_percentage', e.target.value); }}
                    placeholder="5"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="doctor-email"
                  value={formData.email}
                  onChange={function(e) { updateFormField('email', e.target.value); }}
                  placeholder="doctor@clinic.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address</Label>
                <Input
                  id="address"
                  data-testid="doctor-address"
                  value={formData.address}
                  onChange={function(e) { updateFormField('address', e.target.value); }}
                  placeholder="Clinic address"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={function() { setShowAddModal(false); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-mint-600 hover:bg-mint-700" disabled={loading}>
                  {loading ? 'Saving...' : (editingDoctor ? 'Update' : 'Add Doctor')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center">
                <Stethoscope className="w-5 h-5 text-navy-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Doctors</p>
                <p className="text-xl font-bold text-navy-900">{doctors.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-mint-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-mint-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Referrals</p>
                <p className="text-xl font-bold text-navy-900">{totalReferrals}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <IndianRupee className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500">Total Commission</p>
                <p className="text-xl font-bold text-navy-900">₹{totalCommission.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="mb-4">
        <Input
          data-testid="doctor-search"
          placeholder="Search doctors..."
          value={searchTerm}
          onChange={function(e) { setSearchTerm(e.target.value); }}
          className="max-w-md"
        />
      </div>

      {/* Doctors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {renderDoctorCards()}
      </div>
    </div>
  );
}

export default Doctors;
